import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  addAccommodation,
  deleteAccommodationImage,
  fetchAccommodationAccounts,
  fetchAccommodationAddOns,
  fetchAccommodationAddOnsAccounts,
  fetchAccommodationAttributes,
  fetchAccommodationAutoCompleteOptions,
  fetchAccommodationDetails,
  fetchAccommodationDetailsBasic,
  fetchAccommodationImages,
  fetchAccommodationLogs,
  fetchAccommodationNotes,
  fetchAccommodationOwnerValidations,
  getAccommodationCapacities,
  rewriteDescription,
  setAccommodationCapacities,
  fixAccommodationImageIndexes,
  trafsferImagesGrediToCdnAccommodation,
  translateDescription,
  updateAccommodation,
  updateAccommodationAccounts,
  updateAccommodationBasic,
  updateAccommodationImageIndexes,
  updateAccommodationNotes,
  updateAccommodationsAddOns,
  updateAccommodationsAttributes,
  uploadFilesToAzure,
  copyAccommodation,
} from '../../common/api/accommodationsService';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import { AccommodationDetailsAttributeOptionItem } from './accommodationAttributesSlice';

export interface AccommodationAddress {
  postStreetAddress: string;
  additionalAddressLine: string;
  postCode: string;
  postCity: string;
  postCountryId: string;
}
export interface AccommodationDetailsTranslations {
  id: string;
  keyId: string;
  locale: string;
  text: string;
  isDeleted: boolean;
}

export interface AddOnValueDescriptionTranslations {
  id: string;
  keyId: string;
  locale: string;
  text: string;
  isDeleted: boolean;
}

export interface BuildingRoomAreaTranslations {
  keyId: string;
  locale: string;
  text: string;
}

export interface BuildingRoomTranslations {
  keyId: string;
  locale: string;
  text: string;
}

export interface BuildingTranslations {
  keyId: string;
  locale: string;
  text: string;
}

export interface AccommodationUploadImages {
  imagesWithFullPath: File[];
  accommodationId: string;
}

export interface AccommodationImage {
  fileName: string;
  fullUrl: string;
  grediFileId: string;
  index: number;
  modifiedDate: string;
  description: string;
  descriptionEn: string;
  descriptionRu: string;
  altText: string;
  id: string;
}

export interface AccommodationUpdateImages {
  ImageIndexes: AccommodationImage[];
  accommodationId: string;
}
export interface KeyDescriptionValue {
  Text: string;
  Value: boolean;
}

export const NoveltyEnum = {
  NoMeaning: { Value: undefined, Text: 'Ei väliä' } as KeyDescriptionValue,
  Yes: { Value: true, Text: 'Kyllä' } as KeyDescriptionValue,
  No: { Value: false, Text: 'Ei' } as KeyDescriptionValue,
};

export const ShowInWebShopEnum = {
  NoMeaning: { Value: undefined, Text: 'Ei väliä' } as KeyDescriptionValue,
  Yes: { Value: true, Text: 'Kyllä' } as KeyDescriptionValue,
  No: { Value: false, Text: 'Ei' } as KeyDescriptionValue,
};

export const ShowInOwnerPageEnum = {
  NoMeaning: { Value: undefined, Text: 'Ei väliä' } as KeyDescriptionValue,
  Yes: { Value: true, Text: 'Kyllä' } as KeyDescriptionValue,
  No: { Value: false, Text: 'Ei' } as KeyDescriptionValue,
};

export interface AccommodationInternalNote {
  id: string;
  isDeleted: boolean;
  noteKey: string;
  noteText: string;
}

export interface AccommodationDetailsPublicNotes {
  id: string;
  noteText: string;
  visibilityStartsAt: string;
  visibilityEndsAt: string;
}

export interface AccommodationLogEntry {
  userId: string;
  timestamp: string;
  accountId?: string;
  authorFirstName?: string;
  authorLastName?: string;
  targetId?: string;
  targetName?: string;
  oldValues?: unknown;
  newValues?: unknown;
  aggregateId?: string;
  eventType?: string;
}

export interface AccommodationLogListItem {
  currentPage: number;
  data: AccommodationLogEntry[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isSuccess: boolean;
  message: string;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  eventType?: string;
}
export interface AccommodationGroupData {
  id: string;
}

export interface BuildingRoomArea {
  id: string;
  translations: BuildingRoomAreaTranslations[];
}

export interface AccommodationBuilding {
  id: string;
  translations: BuildingTranslations[];
}
export interface AddBuildingRoomArea {
  roomAreaId: string;
  buildingId: string;
}

export interface AddBuilding {
  accommodationId: string;
  buildingId: string;
}

export interface AddBuildingRoom {
  roomId: string;
  buildingId: string;
}

export interface RemoveBuildingRoom {
  id: string;
}

export interface RemoveBuilding {
  id: string;
}

export interface RemoveBuildingRoomArea {
  id: string;
}
export interface BuildingRoom {
  id: string;
  translations: BuildingRoomTranslations[];
}
export interface AccommodationOptionValueItem {
  id: string;
  value: string;
}

export interface AccommodationAddonItem {
  id: string;
  ownerId: string;
  settlementReceiverId: string;
  price: number;
  addOnDefinitionId: number;
  purchasePrice: number;
  amountSingeCharge: boolean;
  amountPerQuantity: boolean;
  amountPerGuest: boolean;
  durationPerNight: boolean;
  percentage: boolean;
  ownerSingleChangePrice: boolean;
  descriptionTranslations: AddOnValueDescriptionTranslations[];
  translations: AddOnValueDescriptionTranslations[];
  isIncludedInPrice: boolean;
  name: string;
  sportumId: string;
  accommodationGroupId: string;
  automatic: boolean;
  addOnNumber: number;
}

export interface AccommodationAttributeItem {
  id?: string;
  intValue?: number;
  floatValue?: number;
  textValue: string;
  booleanValue?: boolean;
  optionValue?: AccommodationOptionValueItem[];
  index?: number;
  buildingRoomAreaId?: string;
  buildingRoomId?: string;
  buildingId?: string;
  multiSelectOptions?: AccommodationDetailsAttributeOptionItem[];
  attributeId?: string;
}
export interface AccommodationGeoCode {
  latitude: number;
  longitude: number;
}
export interface AccommodationAccount {
  id: string;
  accountId: string;
  roleId: string;
  index: number;
}

export interface AccountListItem {
  id: string;
  accountId: string;
  firstName: string;
  hasUserLogin: boolean;
  lastName: string;
  userName: string;
  roleId: string;
  index: number;
  accountNumber: string;
  hasBankIban: boolean;
  hasBankIbanLength: boolean;
  roleName: string;
  hasPersonalIdentityCode: boolean;
}

export enum AttributeGroup {
  Varustelu = 40,
  Mokin_Tiedot = 41,
  Etaisyydet = 42,
  Muut = 43,
}

export interface AccommodationNotes {
  id: string;
  internalNotes: AccommodationInternalNote[];
  publicNotes: AccommodationDetailsPublicNotes[];
}

export interface AccommodationOwnerValidations {
  hasEmail: boolean;
  hasPhone: boolean;
  hasAddress: boolean;
  hasBookingNote: boolean;
}

export interface AccommodationAttributes {
  id: string;
  accommodationAttributes?: AccommodationAttributeItem[];
  distanceResponse: string;
  attributeResponse: string;
  otherResponse: string;
  cottageResponse: string;
}

export interface AccommodationAddOns {
  id: string;
  addOns?: AccommodationAddonItem[];
}
export interface AccommodationDetails {
  id: string;
  accommodationNumber: number;
  beds: number;
  extraBeds: number;
  novelty: boolean;
  translations: AccommodationDetailsTranslations[];
  internalNotes: AccommodationInternalNote[];
  publicNotes: AccommodationDetailsPublicNotes[];
  internalQualityRating: string;
  onSale: boolean;
  showInOwnerPage: boolean;
  qualityRating: number;
  notOnSaleDays: number;
  agentNumber: number;
  visibleOnFi: boolean;
  visibleOnEn: boolean;
  visibleOnRu: boolean;
  keyBox: string;
  countryId: string;
  regionId: string;
  municipalityId: string;
  skiResortId?: string;
  placeId?: string;
  accommodationGroupData: AccommodationGroupData;
  accommodationAttributes?: AccommodationAttributeItem[];
  addOns?: AccommodationAddonItem[];
  accommodationAccountRoleCollection: AccommodationAccount[];
  addressId: string;
  fireSafety: boolean;
  optiWatti: boolean;
  leviItem: boolean;
  interhomeService: boolean;
  lastMinuteOffer: boolean;
  sportumId: string;
  geoCode: AccommodationGeoCode;
  accounts: AccountListItem[];
  useGredi: boolean;
  isCapacityProduct: boolean;
  bedRooms?: number;
  area?: number;
}

export interface AccommodationDetailsBasic {
  id: string;
  accommodationNumber: number;
  beds: number;
  extraBeds: number;
  novelty: boolean;
  translations: AccommodationDetailsTranslations[];
  internalQualityRating: string;
  onSale: boolean;
  showInOwnerPage: boolean;
  qualityRating: number;
  notOnSaleDays: number;
  agentNumber: number;
  visibleOnFi: boolean;
  visibleOnEn: boolean;
  visibleOnRu: boolean;
  keyBox: string;
  countryId: string;
  regionId: string;
  municipalityId: string;
  skiResortId?: string;
  placeId?: string;
  tagId: string;
  accommodationGroupData: AccommodationGroupData;
  addressId: string;
  fireSafety: boolean;
  optiWatti: boolean;
  leviItem: boolean;
  interhomeService: boolean;
  lastMinuteOffer: boolean;
  sportumId: string;
  geoCode: AccommodationGeoCode;
  useGredi: boolean;
  isCapacityProduct: boolean;
  bedRooms?: number;
  area?: number;
  isAllowedToEdit: boolean;
  salesRewardP: number;
  priceCommissionP: number;
  balanceCommissionP: number;
  travelAgencyDomain?: string;
  travelAgenciesWithDiscounts?: string[];
  acceptedBySellerSite: boolean;
  offeredToSellerSite: boolean;
  sellerSiteComments: string;
  allowOffer: boolean;
  createdAt: Date;
  updatedAt: Date;
  capacityIds?: string[];
}
export interface DeleteAccommodationImage {
  accommodationId: string;
  imageId: string;
}
export interface AccommodationDetailsUpdateBasic {
  id: string;
  accommodationNumber: number;
  beds: number;
  extraBeds: number;
  novelty: boolean;
  translations: AccommodationDetailsTranslations[];
  internalQualityRating: string;
  onSale: boolean;
  showInOwnerPage: boolean;
  qualityRating: number;
  notOnSaleDays: number;
  agentNumber: number;
  visibleOnFi: boolean;
  visibleOnEn: boolean;
  visibleOnRu: boolean;
  keyBox: string;
  accommodationGroupDataId: string;
  countryId: string;
  regionId: string;
  municipalityId: string;
  skiResortId?: string;
  placeId?: string;
  tagId: string;
  fireSafety: boolean;
  geoCode: AccommodationGeoCode;
  optiWatti: boolean;
  leviItem: boolean;
  interhomeService: boolean;
  lastMinuteOffer: boolean;
  sportumId: string;
  isCapacityProduct: boolean;
  bedRooms?: number;
  area?: number;
  SalesRewardPerCent: number;
  PriceCommissionPerCent: number;
  BalanceCommissionPerCent: number;
  travelAgencyDomain?: string;
  travelAgenciesWithDiscounts?: string[];
  acceptedBySellerSite: boolean;
  offeredToSellerSite: boolean;
  sellerSiteComments: string;
}
export interface AccommodationDetailsUpdate {
  id: string;
  accommodationNumber: number;
  beds: number;
  extraBeds: number;
  novelty: boolean;
  translations: AccommodationDetailsTranslations[];
  internalNotes: AccommodationInternalNote[];
  publicNotes: AccommodationDetailsPublicNotes[];
  attributes: AccommodationAttributeItem[];
  internalQualityRating: string;
  onSale: boolean;
  showInOwnerPage: boolean;
  qualityRating: number;
  notOnSaleDays: number;
  agentNumber: number;
  visibleOnFi: boolean;
  visibleOnEn: boolean;
  visibleOnRu: boolean;
  keyBox: string;
  accommodationGroupDataId: string;
  countryId: string;
  regionId: string;
  municipalityId: string;
  skiResortId?: string;
  placeId?: string;
  addOns: AccommodationAddonItem[];
  updateAccommodationAccountRoleCollection: AccommodationAccount[];
  fireSafety: boolean;
  geoCode: AccommodationGeoCode;
  optiWatti: boolean;
  leviItem: boolean;
  interhomeService: boolean;
  lastMinuteOffer: boolean;
  sportumId: string;
  isCapacityProduct: boolean;
  bedRooms?: number;
  area?: number;
}

export interface AccommodationAccountsUpdate {
  id: string;
  updateAccommodationAccountRoleCollection: AccommodationAccount[];
}

export interface AccommodationAttributesUpdate {
  id: string;
  attributes: AccommodationAttributeItem[];
}

export interface AccommodationAddOnsUpdate {
  id: string;
  addOns: AccommodationAddonItem[];
}

export interface AccommodationNotesUpdate {
  id: string;
  translations: AccommodationDetailsTranslations[];
  internalNotes: AccommodationInternalNote[];
  publicNotes: AccommodationDetailsPublicNotes[];
}

export interface AccommodationAutoCompleteOption {
  accommodationId: string;
  accommodationNumber: number;
  name?: string;
}

export interface AccommodationFilteredHistoryLogsRequest {
  AggregateId?: string;
  PageNumber: number;
  PageSize: number;
}

export interface SetCapacitiesCommand {
  accommodationId: string;
  capacitiesIdDataDto: {
    capacityIds: string[];
    capacityType: string;
    parentId: string;
  };
}

export interface GetCapacitiesResponse {
  accommodationId: string;
  capacityIds: string[];
}

export interface CopyAccommodationCommand {
  sourceAccommodationId: string;
  copyAddons: boolean;
  copyOwners: boolean;
  copyMaintenance: boolean;
  copyBuildings: boolean;
}

export interface AccommodationsState {
  accommodationLogs: AccommodationLogListItem;
  accommodationImages: AccommodationImage[];
  accommodationDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationImagesStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationLogsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationDetails: AccommodationDetails;
  accommodationDetailsBasic: AccommodationDetailsBasic;
  accommodationLogFilter: AccommodationFilteredHistoryLogsRequest;
  accommodationAccounts: AccountListItem[];
  accommodationAddOns: AccommodationAddOns;
  accommodationAddOnsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationAccountsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationNotesStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationNotes: AccommodationNotes;
  accommodationAttributesLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationOwnerValidationsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationOwnerValidations: AccommodationOwnerValidations;
  accommodationAttributes: AccommodationAttributes;
  accommodationCapacities: GetCapacitiesResponse | null;
  accommodationCapacitiesLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

export interface RewriteDescriptionCommand {
  description: string;
}

export interface TranslatePropertiesCommand {
  text: string;
  targetLanguage: string;
}

const initialState: AccommodationsState = {
  accommodationAddOns: null,
  accommodationAddOnsLoadingStatus: LOADING_STATUS.IDLE,
  accommodationNotesStatus: LOADING_STATUS.IDLE,
  accommodationNotes: null,
  accommodationLogs: null,
  accommodationImages: [],
  accommodationLogsStatus: LOADING_STATUS.IDLE,
  accommodationDetailsStatus: LOADING_STATUS.IDLE,
  accommodationImagesStatus: LOADING_STATUS.IDLE,
  accommodationLogFilter: {
    AggregateId: null,
    PageSize: 20,
    PageNumber: 1,
  },
  accommodationOwnerValidationsLoadingStatus: LOADING_STATUS.IDLE,
  accommodationOwnerValidations: null,
  accommodationDetails: {
    id: '',
    accommodationNumber: 0,
    beds: 0,
    extraBeds: 0,
    novelty: null,
    translations: [],
    internalQualityRating: '',
    onSale: null,
    showInOwnerPage: null,
    qualityRating: 0,
    notOnSaleDays: 0,
    agentNumber: 0,
    visibleOnFi: null,
    visibleOnRu: null,
    visibleOnEn: null,
    keyBox: null,
    countryId: '',
    regionId: '',
    municipalityId: '',
    internalNotes: [],
    publicNotes: [],
    accommodationGroupData: {
      id: '',
    },
    accommodationAttributes: [],
    accommodationAccountRoleCollection: [],
    addressId: null,
    fireSafety: false,
    geoCode: {
      latitude: null,
      longitude: null,
    },
    optiWatti: false,
    leviItem: false,
    interhomeService: false,
    lastMinuteOffer: false,
    sportumId: '',
    accounts: [],
    useGredi: false,
    isCapacityProduct: false,
    bedRooms: 0,
  },
  accommodationDetailsBasic: {
    id: '',
    accommodationNumber: 0,
    beds: 0,
    extraBeds: 0,
    novelty: null,
    translations: [],
    internalQualityRating: '',
    onSale: null,
    showInOwnerPage: null,
    qualityRating: 0,
    notOnSaleDays: 0,
    agentNumber: 0,
    visibleOnFi: null,
    visibleOnRu: null,
    visibleOnEn: null,
    keyBox: null,
    countryId: '',
    regionId: '',
    municipalityId: '',
    tagId: '',
    accommodationGroupData: {
      id: '',
    },
    addressId: null,
    fireSafety: false,
    geoCode: {
      latitude: null,
      longitude: null,
    },
    optiWatti: false,
    leviItem: false,
    interhomeService: false,
    lastMinuteOffer: false,
    sportumId: '',
    useGredi: false,
    isCapacityProduct: false,
    bedRooms: 0,
    isAllowedToEdit: false,
    balanceCommissionP: 0,
    placeId: '',
    priceCommissionP: 0,
    salesRewardP: 0,
    skiResortId: '',
    area: 0,
    travelAgencyDomain: null,
    travelAgenciesWithDiscounts: null,
    offeredToSellerSite: false,
    sellerSiteComments: '',
    acceptedBySellerSite: false,
    allowOffer: false,
    createdAt: null,
    updatedAt: null,
    capacityIds: [],
  },
  accommodationAccounts: [],
  accommodationAccountsLoadingStatus: LOADING_STATUS.IDLE,
  accommodationAttributes: null,
  accommodationAttributesLoadingStatus: LOADING_STATUS.IDLE,
  accommodationCapacities: null,
  accommodationCapacitiesLoadingStatus: LOADING_STATUS.IDLE,
};

export const getAccommodationAutoCompleteOptionsAsync = createAsyncThunk(
  'accommodations/getAccommodationAutoCompleteOptions',
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationAutoCompleteOptions(searchTerm);
      return response.data;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationDetailsAsync = createAsyncThunk(
  'accommodations/getAccommodationDetails',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationDetails(accommodationId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationAccountsAsync = createAsyncThunk(
  'accommodations/getAccommodationAccounts',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationAccounts(accommodationId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationAddOnsAccountsAsync = createAsyncThunk(
  'accommodations/getAccommodationAddOnsAccounts',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationAddOnsAccounts(accommodationId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationAddOnsAsync = createAsyncThunk(
  'accommodations/getAccommodationAddOns',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationAddOns(accommodationId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationAttributesAsync = createAsyncThunk(
  'accommodations/getAccommodationAttributes',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationAttributes(accommodationId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationOwnerValidationsAsync = createAsyncThunk(
  'accommodations/getAccommodationOwnerValidations',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchAccommodationOwnerValidations(accommodationId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationNotesAsync = createAsyncThunk(
  'accommodations/getAccommodationNotes',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationNotes(accommodationId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);
export const getAccommodationDetailsBasicAsync = createAsyncThunk(
  'accommodations/getAccommodationDetailsBasic',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationDetailsBasic(accommodationId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationImagesAsync = createAsyncThunk(
  'accommodations/getAccommodationImages',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationImages(accommodationId);
      return response.data.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);
export const getAccommodationLogsAsync = createAsyncThunk(
  'accommodations/getAccommodationLogs',
  async (
    accommodationLogRequest: AccommodationFilteredHistoryLogsRequest,
    { rejectWithValue },
  ) => {
    try {
      return await fetchAccommodationLogs(accommodationLogRequest);
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveAccommodationAsync = createAsyncThunk(
  'accommodations/addAccommodation',
  async (accommodationData: AccommodationDetails, { rejectWithValue }) => {
    try {
      const response = await addAccommodation(accommodationData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAccommodationAsync = createAsyncThunk(
  'accommodations/updateAccommodation',
  async (
    accommodationData: AccommodationDetailsUpdate,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateAccommodation(accommodationData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAccommodationAccountsAsync = createAsyncThunk(
  'accommodations/updateAccommodationAccounts',
  async (
    accommodationData: AccommodationAccountsUpdate,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateAccommodationAccounts(accommodationData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAccommodationsAttributesAsync = createAsyncThunk(
  'accommodations/updateAccommodationsAttributes',
  async (
    accommodationData: AccommodationAttributesUpdate,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateAccommodationsAttributes(accommodationData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAccommodationsAddOnsAsync = createAsyncThunk(
  'accommodations/updateAccommodationsAddOns',
  async (accommodationData: AccommodationAddOnsUpdate, { rejectWithValue }) => {
    try {
      const response = await updateAccommodationsAddOns(accommodationData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const updateAccommodationNotesAsync = createAsyncThunk(
  'accommodations/updateAccommodationNotes',
  async (accommodationData: AccommodationNotesUpdate, { rejectWithValue }) => {
    try {
      const response = await updateAccommodationNotes(accommodationData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const updateAccommodatioBasicAsync = createAsyncThunk(
  'accommodations/updateAccommodation',
  async (
    accommodationData: AccommodationDetailsUpdateBasic,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateAccommodationBasic(accommodationData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAccommodationImageIndexesAsync = createAsyncThunk(
  'accommodations/updateAccommodationImageIndexesAsync',
  async (accommodationData: AccommodationUpdateImages, { rejectWithValue }) => {
    try {
      const response = await updateAccommodationImageIndexes(accommodationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteAccommodationImageAsync = createAsyncThunk(
  'accommodations/deleteAccommodationImage',
  async (deleteCommand: DeleteAccommodationImage, { rejectWithValue }) => {
    try {
      const response = await deleteAccommodationImage(
        deleteCommand.imageId,
        deleteCommand.accommodationId,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const rewriteDescriptionAsync = createAsyncThunk(
  'accommodations/rewriteDescription',
  async (
    rewriteDescriptionCommand: RewriteDescriptionCommand,
    { rejectWithValue },
  ) => {
    try {
      const response = await rewriteDescription(rewriteDescriptionCommand);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const translateDescriptionAsync = createAsyncThunk(
  'accommodations/translateProperties',
  async (
    translateDescriptionCommand: TranslatePropertiesCommand,
    { rejectWithValue },
  ) => {
    try {
      const response = await translateDescription(translateDescriptionCommand);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const tranferImagesAccommodationAsync = createAsyncThunk(
  'accommodations/tranferImagesAccommodation',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await trafsferImagesGrediToCdnAccommodation(
        accommodationId.toUpperCase(),
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fixAccommodationImageIndexesAsync = createAsyncThunk(
  'accommodations/fixAccommodationImageIndexes',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fixAccommodationImageIndexes(accommodationId);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);
export const uploadFilesToAzureAsync = createAsyncThunk(
  'accommodations/uploadFilesToAzure',
  async (updateCommand: AccommodationUploadImages, { rejectWithValue }) => {
    try {
      return await uploadFilesToAzure(updateCommand);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getAccommodationCapacitiesAsync = createAsyncThunk(
  'accommodations/getAccommodationCapacities',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await getAccommodationCapacities(accommodationId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const setAccommodationCapacitiesAsync = createAsyncThunk(
  'accommodations/setCapacities',
  async (command: SetCapacitiesCommand, { rejectWithValue }) => {
    try {
      const response = await setAccommodationCapacities(command);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyAccommodationAsync = createAsyncThunk(
  'accommodations/copyAccommodation',
  async (copyCommand: CopyAccommodationCommand, { rejectWithValue }) => {
    try {
      const response = await copyAccommodation(copyCommand);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const accommodationsSlice = createSlice({
  name: 'accommodations',
  initialState,
  reducers: {
    clearImages: state => {
      state.accommodationImages = [];
      state.accommodationImagesStatus = LOADING_STATUS.IDLE;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getAccommodationDetailsAsync.pending, state => {
        state.accommodationDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationDetailsBasicAsync.pending, state => {
        state.accommodationDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationLogsAsync.pending, state => {
        state.accommodationLogsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationLogsAsync.rejected, state => {
        state.accommodationLogsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationLogsAsync.fulfilled, (state, action) => {
        state.accommodationLogsStatus = LOADING_STATUS.IDLE;
        state.accommodationLogs = action.payload;
      })
      .addCase(getAccommodationDetailsAsync.fulfilled, (state, action) => {
        state.accommodationDetailsStatus = LOADING_STATUS.IDLE;
        state.accommodationDetails = action.payload;
      })
      .addCase(getAccommodationDetailsBasicAsync.fulfilled, (state, action) => {
        state.accommodationDetailsStatus = LOADING_STATUS.IDLE;
        state.accommodationDetailsBasic = action.payload;
      })
      .addCase(getAccommodationDetailsAsync.rejected, state => {
        state.accommodationDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationDetailsBasicAsync.rejected, state => {
        state.accommodationDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(updateAccommodationAsync.rejected, state => {
        state.accommodationDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(updateAccommodationAsync.pending, state => {
        state.accommodationDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(updateAccommodationAsync.fulfilled, state => {
        state.accommodationDetailsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getAccommodationImagesAsync.pending, state => {
        state.accommodationImagesStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationImagesAsync.rejected, state => {
        state.accommodationImagesStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationImagesAsync.fulfilled, (state, action) => {
        state.accommodationImagesStatus = LOADING_STATUS.IDLE;
        state.accommodationImages = action.payload;
      })
      .addCase(getAccommodationAccountsAsync.pending, state => {
        state.accommodationAccountsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationAccountsAsync.rejected, state => {
        state.accommodationAccountsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationAccountsAsync.fulfilled, (state, action) => {
        state.accommodationAccountsLoadingStatus = LOADING_STATUS.IDLE;
        state.accommodationAccounts = action.payload;
      })
      .addCase(getAccommodationAddOnsAccountsAsync.pending, state => {
        state.accommodationAccountsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationAddOnsAccountsAsync.rejected, state => {
        state.accommodationAccountsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(
        getAccommodationAddOnsAccountsAsync.fulfilled,
        (state, action) => {
          state.accommodationAccountsLoadingStatus = LOADING_STATUS.IDLE;
          state.accommodationAccounts = action.payload;
        },
      )
      .addCase(getAccommodationAddOnsAsync.pending, state => {
        state.accommodationAddOnsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationAddOnsAsync.rejected, state => {
        state.accommodationAddOnsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationAddOnsAsync.fulfilled, (state, action) => {
        state.accommodationAddOnsLoadingStatus = LOADING_STATUS.IDLE;
        state.accommodationAddOns = action.payload;
      })
      .addCase(getAccommodationAttributesAsync.pending, state => {
        state.accommodationAttributesLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationAttributesAsync.rejected, state => {
        state.accommodationAttributesLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationAttributesAsync.fulfilled, (state, action) => {
        state.accommodationAttributesLoadingStatus = LOADING_STATUS.IDLE;
        state.accommodationAttributes = action.payload;
      })
      .addCase(getAccommodationOwnerValidationsAsync.pending, state => {
        state.accommodationOwnerValidationsLoadingStatus =
          LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationOwnerValidationsAsync.rejected, state => {
        state.accommodationOwnerValidationsLoadingStatus =
          LOADING_STATUS.FAILED;
      })
      .addCase(
        getAccommodationOwnerValidationsAsync.fulfilled,
        (state, action) => {
          state.accommodationOwnerValidationsLoadingStatus =
            LOADING_STATUS.IDLE;
          state.accommodationOwnerValidations = action.payload;
        },
      )

      .addCase(getAccommodationNotesAsync.pending, state => {
        state.accommodationNotesStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationNotesAsync.rejected, state => {
        state.accommodationNotesStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationNotesAsync.fulfilled, (state, action) => {
        state.accommodationNotesStatus = LOADING_STATUS.IDLE;
        state.accommodationNotes = action.payload;
      })
      .addCase(getAccommodationCapacitiesAsync.pending, state => {
        state.accommodationCapacitiesLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationCapacitiesAsync.rejected, state => {
        state.accommodationCapacitiesLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationCapacitiesAsync.fulfilled, (state, action) => {
        state.accommodationCapacitiesLoadingStatus = LOADING_STATUS.IDLE;
        state.accommodationCapacities = action.payload;
      });
  },
});
export const selectAccommodationAccounts = (state: RootState) =>
  state.accommodations.accommodationAccounts;

export const selectAccommodationAccountsLoadingStatus = (state: RootState) =>
  state.accommodations.accommodationAccountsLoadingStatus;

export const selectAccommodationAddOns = (state: RootState) =>
  state.accommodations.accommodationAddOns;

export const selectAccommodationAddOnsLoadingStatus = (state: RootState) =>
  state.accommodations.accommodationAddOnsLoadingStatus;

export const selectAccommodationsAttributes = (state: RootState) =>
  state.accommodations.accommodationAttributes;

export const selectAccommodationsAttributesLoadingStatus = (state: RootState) =>
  state.accommodations.accommodationAttributesLoadingStatus;

export const selectAccommodationNotes = (state: RootState) =>
  state.accommodations.accommodationNotes;

export const selectAccommodationNotesLoadingStatus = (state: RootState) =>
  state.accommodations.accommodationNotes;
export const selectAccommodationDetails = (state: RootState) =>
  state.accommodations.accommodationDetails;

export const selectAccommodationDetailsBasic = (state: RootState) =>
  state.accommodations.accommodationDetailsBasic;

export const selectAccommodationOwnerValidations = (state: RootState) =>
  state.accommodations.accommodationOwnerValidations;

export const selectAccommodationOwnerValidationsLoadingStatus = (
  state: RootState,
) => state.accommodations.accommodationOwnerValidationsLoadingStatus;

export const selectAccommodationLogs = (state: RootState) =>
  state.accommodations.accommodationLogs;
export const selectAccommodationDetailsLoadingStatus = (state: RootState) =>
  state.accommodations.accommodationDetailsStatus;
export const selectAccommodationLogsLoadingStatus = (state: RootState) =>
  state.accommodations.accommodationLogsStatus;

export const selectAccommodationImagesLoadingStatus = (state: RootState) =>
  state.accommodations.accommodationImagesStatus;

export const selectAccommodationImages = (state: RootState) =>
  state.accommodations.accommodationImages;

export const selectAccommodationCapacities = (state: RootState) =>
  state.accommodations.accommodationCapacities;

export const selectAccommodationCapacitiesLoadingStatus = (state: RootState) =>
  state.accommodations.accommodationCapacitiesLoadingStatus;

export default accommodationsSlice.reducer;
